import { signIn } from 'next-auth/react';
import React, { useEffect, useState } from 'react';

import { useMetrics } from '../../../hooks/useMetrics';
import { buildSignupSuccessCallback } from '../../../utils/adsConversionTrackingUtils';
import { AUTH_EVENTS, clearAuthPending, setAuthPending } from '../../../utils/authFunnel';
import { isUnSupportedBrowser } from '../../../utils/navigatorUtils';
import {
  ChevIco,
  ClassLinkIco,
  CleverIco,
  FeideIco,
  GoogleIco,
  MailIco,
} from './AuthIcons';
import {
  AuthHead,
  AuthTitle,
  Field,
  FieldErr,
  FieldGroup,
  FieldHint,
  FieldLabel,
  Legal,
  OAuthTile,
  OAuthTiles,
  OrDivider,
  OtherProvidersLink,
  PrimaryBtn,
  SupportRef,
  SwitchLine,
} from './authStyles';
import MagicLinkCodeConfirm from './MagicLinkCodeConfirm';
import { AuthStrings } from './useAuthStrings';

const isEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export type AuthMode = 'login' | 'signup';

type Props = {
  t: AuthStrings;
  mode: AuthMode;
  setMode: (m: AuthMode) => void;
  callbackUrl: string;
};

/**
 * Inline email-first auth form: the magic-link email field is shown immediately,
 * with SSO providers below. All providers use the new (non-Azure) NextAuth
 * provider ids. The magic-link send/verify is owned by the backend (CURI-334);
 * the UI calls the Email provider and a code-verify endpoint and degrades
 * gracefully until that backend is live.
 */
const AuthForm: React.FC<Props> = ({ t, mode, setMode, callbackUrl }) => {
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  // Support reference (backend correlation id) shown when a code can't be sent.
  const [errRef, setErrRef] = useState<string | undefined>();
  const [showMore, setShowMore] = useState(false);
  const [sending, setSending] = useState(false);
  // In-app browsers (Facebook / TikTok / LinkedIn) block or break third-party
  // OAuth pop-ups, so we hide the SSO providers and offer only the magic link
  // there. Detected after mount (navigator is client-only) to avoid a hydration
  // mismatch — defaults to false so SSR renders the full set.
  const [oauthUnsupported, setOauthUnsupported] = useState(false);
  const metrics = useMetrics(true);

  useEffect(() => {
    setOauthUnsupported(isUnSupportedBrowser());
  }, []);

  const isSignup = mode === 'signup';

  // On signup, route the post-auth destination through /signup/success so
  // ad-conversion (CompleteRegistration) + analytics fire — matching legacy
  // useSignUp. Login goes straight to the destination. Pure (no side effects).
  const effectiveCallbackUrl = (dest: string) =>
    isSignup ? buildSignupSuccessCallback(dest) : dest;

  // Log the Signup funnel event once per signup initiation (provider click or
  // code request) — not on every magic-link verify retry, which would inflate it.
  const logSignupInitiated = () => {
    if (isSignup) metrics.logEvent('Signup', undefined, true);
  };

  useEffect(() => {
    setStep('form');
    setErr('');
    // Toggling login↔signup means the user reconsidered, so any in-flight pending
    // marker (from a started OAuth attempt or a sent magic-link code) is now stale
    // — drop it so the next concrete action re-sets it with the current mode and
    // Auth.Succeeded isn't attributed to the wrong mode/method.
    clearAuthPending();
  }, [mode]);
  useEffect(() => {
    setErr('');
    setErrRef(undefined);
  }, [email]);

  const onProvider = (provider: 'google' | 'clever' | 'classlink' | 'feide') => {
    // Explicitly use the exchange flow (not self-join) — overwrites any stale
    // self-join cookie from a prior visit to an SSO entry page. Same-origin
    // top-level GET → SameSite=Lax is sent. The backend infers signup vs login,
    // so there's no intent to carry through.
    document.cookie = 'curipod_auth_flow=exchange; path=/; max-age=600; samesite=lax';
    metrics.logEvent(AUTH_EVENTS.methodSelected, { mode, method: 'oauth', provider });
    // Remember the attempt so Auth.Succeeded can fire after the OAuth redirect.
    setAuthPending({ mode, method: 'oauth', provider });
    logSignupInitiated();
    signIn(provider, { callbackUrl: effectiveCallbackUrl(callbackUrl) });
  };

  // Request a 6-digit magic-link code (proxied to the backend's public
  // /auth/magic-link/request, CURI-334). Returns the outcome — plus the backend
  // correlation id on failure so the UI can show a support reference.
  const requestCode = async (): Promise<{
    status: 'ok' | 'rate-limited' | 'error';
    correlationId?: string;
  }> => {
    try {
      const res = await fetch('/api/auth/magic-link/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      if (res.status === 429) return { status: 'rate-limited' };
      if (res.ok) return { status: 'ok' };
      let correlationId: string | undefined;
      try {
        correlationId = (await res.json())?.correlationId;
      } catch {
        // body not JSON — no reference to show
      }
      return { status: 'error', correlationId };
    } catch {
      return { status: 'error' };
    }
  };

  const sendLink = async () => {
    if (sending) return; // in-flight guard against rapid clicks / repeated Enter
    if (!isEmail(email)) {
      setErr(t.invalidEmail);
      setErrRef(undefined);
      return;
    }
    // Funnel: the user committed to the magic-link method.
    metrics.logEvent(AUTH_EVENTS.methodSelected, { mode, method: 'magicLink' });
    setSending(true);
    try {
      const { status, correlationId } = await requestCode();
      if (status === 'rate-limited') {
        metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: 'rate-limited' });
        setErr(t.errTooManyRequests);
        setErrRef(undefined);
        return;
      }
      if (status === 'error') {
        metrics.logEvent(AUTH_EVENTS.sendFailed, { mode, reason: 'error' });
        setErr(t.errSendCodeFailed);
        setErrRef(correlationId);
        return;
      }
      // Count the signup once, when the code was actually sent.
      logSignupInitiated();
      metrics.logEvent(AUTH_EVENTS.codeSent, { mode });
      setAuthPending({ mode, method: 'magicLink' });
      setStep('code');
    } finally {
      setSending(false);
    }
  };

  // Verify the code via the `magic-link` CredentialsProvider, which calls the
  // backend and establishes the NextAuth session on success.
  const verifyCode = async (code: string) => {
    // NextAuth runs `new URL(data.url)` for credentials + redirect:false, and our
    // redirect callback passes the URL through unchanged — so the callbackUrl must
    // be absolute, or a relative "/" throws "Failed to construct 'URL'".
    const absoluteCallback = new URL(
      effectiveCallbackUrl(callbackUrl),
      window.location.origin,
    ).href;
    const res = await signIn('magic-link', {
      email: email.trim(),
      code,
      redirect: false,
      callbackUrl: absoluteCallback,
    });
    if (!res || res.error) {
      throw new Error(t.errCodeInvalid);
    }
    window.location.href = res.url || absoluteCallback;
  };

  if (step === 'code') {
    return (
      <MagicLinkCodeConfirm
        t={t}
        email={email}
        mode={mode}
        onVerify={verifyCode}
        onResend={requestCode}
        onTryAnother={() => setStep('form')}
        onChangeEmail={() => setStep('form')}
      />
    );
  }

  return (
    <>
      <AuthHead>
        <AuthTitle>{isSignup ? t.signupTitle : t.loginTitle}</AuthTitle>
      </AuthHead>

      <FieldGroup>
        <FieldLabel htmlFor="email">{t.email}</FieldLabel>
        <Field
          id="email"
          type="email"
          $err={!!err}
          placeholder={t.emailPh}
          value={email}
          onChange={e => setEmail(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') sendLink();
          }}
          autoComplete="email"
        />
        {err ? (
          <FieldErr>
            {err}
            {errRef ? (
              <SupportRef>
                {t.errSupportRef}: <code>{errRef}</code>
              </SupportRef>
            ) : null}
          </FieldErr>
        ) : email.trim() ? (
          <FieldHint>
            <MailIco />
            {t.secureHint}
          </FieldHint>
        ) : null}
      </FieldGroup>

      <PrimaryBtn onClick={sendLink} disabled={!email.trim() || sending}>
        {t.continue}
      </PrimaryBtn>

      {/* In-app browsers (Facebook / TikTok / LinkedIn) block third-party OAuth
          pop-ups, so the SSO providers are hidden there — magic link only. */}
      {!oauthUnsupported && (
        <>
          <OrDivider>{t.orContinue}</OrDivider>

          <OAuthTiles $cols={3}>
            <OAuthTile onClick={() => onProvider('google')}>
              <GoogleIco />
              <span>Google</span>
            </OAuthTile>
            <OAuthTile onClick={() => onProvider('clever')}>
              <CleverIco />
              <span>Clever</span>
            </OAuthTile>
            <OAuthTile onClick={() => onProvider('classlink')}>
              <ClassLinkIco />
              <span>ClassLink</span>
            </OAuthTile>
          </OAuthTiles>
          {showMore && (
            <OAuthTiles $cols={3} className="more-tiles">
              <OAuthTile onClick={() => onProvider('feide')}>
                <FeideIco />
                <span>Feide</span>
              </OAuthTile>
            </OAuthTiles>
          )}
          <OtherProvidersLink onClick={() => setShowMore(m => !m)}>
            {showMore ? t.hideOther : t.otherProviders}
            <ChevIco className={showMore ? 'up' : ''} />
          </OtherProvidersLink>
        </>
      )}

      <SwitchLine>
        {isSignup ? t.haveAccount : t.newHere}{' '}
        <button onClick={() => setMode(isSignup ? 'login' : 'signup')}>
          {isSignup ? t.login : t.signup}
        </button>
      </SwitchLine>

      {isSignup && (
        <Legal>
          {t.legalPre}{' '}
          <a href="/terms-of-use" target="_blank" rel="noreferrer">
            {t.terms}
          </a>{' '}
          {t.and}{' '}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            {t.privacy}
          </a>
          {t.legalPost}
        </Legal>
      )}
    </>
  );
};

export default AuthForm;
